import * as React from "react";
import { useForm } from "react-hook-form";
import {
    FormButton,
    SearchInput,
    Button,
    PopoverContent,
    Popover,
    PopoverTrigger,
    useToast,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useAddSettingsChanges } from "../../../practice-settings/hooks/mutations/practise-settings";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { useDebounce } from "../../../../hooks/helpers";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { IAddSettingChanges } from "../../../practice-settings/types/practice-settings";
import { useFetchNoteAccessOverrides } from "../../hooks/note-access-overrides.queries";

interface AddNoteAccessOverrideFormProps {
    onFormSubmit: () => void;
}

type ComboOption = {
    value: string;
    label: string;
};

export default function AddNoteAccessOverrideForm({
    onFormSubmit,
}: AddNoteAccessOverrideFormProps) {
    const [providerSearchString, setProviderSearchString] = React.useState("");
    const [selectedProvider, setSelectedProvider] =
        React.useState<ComboOption | null>(null);
    const [showProvidersListPopover, setShowProvidersListPopover] =
        React.useState(false);

    const debouncedProviderSearchString = useDebounce(
        providerSearchString,
        500
    );

    const { handleSubmit } = useForm<IAddSettingChanges>({});

    const providersListContainerRef = React.useRef<HTMLDivElement>(null);

    const { data, isLoading, error, isSuccess } = useFetchProviders({
        page: 1,
        limit: 20,
        search_string: debouncedProviderSearchString,
    });

    const addNewSettings = useAddSettingsChanges();

    const { data: noteAccessOverrides } = useFetchNoteAccessOverrides();

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const existingProviderIds =
        noteAccessOverrides?.data?.providers.map(
            (p) => p.provider.provider_id
        ) ?? [];

    const providersOptions = data?.data.filter(
        (provider) => !existingProviderIds.includes(provider.provider_id)
    );

    const onSubmit = async () => {
        if (!selectedProvider) {
            toast({ mode: "error", message: "Provider field cannot be empty" });
            return;
        }

        const providerIdsToSend =
            existingProviderIds.length > 0
                ? JSON.stringify([
                      selectedProvider.value,
                      ...existingProviderIds,
                  ])
                : JSON.stringify([selectedProvider.value]);

        addNewSettings.mutate(
            {
                setting_id: noteAccessOverrides?.data.setting_id ?? "",
                current_value: providerIdsToSend,
            },
            {
                onSuccess: () => {
                    toast({
                        mode: "success",
                        message: "Note access override added successfully",
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["note-access-overrides"],
                    });
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Error adding note access override",
                    });
                },
            }
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} id="add-note-access-override">
            <div className="flex-1" ref={providersListContainerRef}>
                <Popover
                    open={showProvidersListPopover}
                    onOpenChange={setShowProvidersListPopover}
                >
                    <PopoverTrigger
                        asChild
                        onClick={() => setShowProvidersListPopover(true)}
                        className="w-full"
                    >
                        <FormButton
                            label="Select a provider"
                            value={selectedProvider?.label ?? ""}
                            placeholder="Select a provider"
                        />
                    </PopoverTrigger>

                    <PopoverContent
                        className="popover-full"
                        align="start"
                        container={providersListContainerRef.current}
                    >
                        <SearchInput
                            placeholder="Search by provider name"
                            onChange={(e) =>
                                setProviderSearchString(e.target.value)
                            }
                        />

                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                            {isLoading && (
                                <Loader wrapperClassName="h-[150px]" />
                            )}

                            {error && (
                                <ListState
                                    isError
                                    context="general"
                                    stateHelperText="Please try again later"
                                    errorMsg={
                                        error?.response?.data?.message ||
                                        "Error loading providers"
                                    }
                                    cardClass="h-[150px]"
                                />
                            )}

                            {data?.data && providersOptions && isSuccess && (
                                <>
                                    <Button
                                        size="auto"
                                        variant="can-hover"
                                        className="font-medium capitalize w-full justify-start my-1.5 shrink-0 text-danger"
                                        onClick={() => {
                                            setProviderSearchString("");
                                            setSelectedProvider(null);
                                            setShowProvidersListPopover(false);
                                        }}
                                    >
                                        Clear selected
                                    </Button>
                                    {providersOptions.map((provider) => (
                                        <Button
                                            key={provider.provider_id}
                                            size="auto"
                                            variant="can-hover"
                                            className="font-medium capitalize w-full justify-start my-1 shrink-0"
                                            onClick={() => {
                                                setSelectedProvider({
                                                    label: provider.provider_name,
                                                    value: provider.provider_id,
                                                });
                                                setShowProvidersListPopover(
                                                    false
                                                );
                                            }}
                                        >
                                            {provider.provider_name}
                                        </Button>
                                    ))}
                                </>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </form>
    );
}
